import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { Home, Briefcase, Settings, Trash2, X, Loader2, Check, MapPin as MapPinIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { reverseGeocode } from "../lib/location";

// Reused pin icon so the editor's mini-map matches the request sheet.
const dragPinIcon = L.divIcon({
  className: "edit-pin",
  html: `<div style="width:22px;height:22px;transform:translate(-50%,-50%);"><div style="width:100%;height:100%;border-radius:9999px;background:#00E5FF;box-shadow:0 0 0 4px rgba(0,229,255,0.25),0 4px 12px rgba(0,0,0,0.55);"></div></div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

function CenterOn({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, map.getZoom() || 15, { animate: true }); }, [center?.[0], center?.[1]]); // eslint-disable-line
  return null;
}

/**
 * SavedPlaceQuickChip
 * ───────────────────
 * The big pill button under the address inputs for one-tap Home / Work fill.
 * Also includes a tiny gear that opens an inline editor:
 *   - Rename the label
 *   - Delete the saved place
 * Parent supplies onFill (address + coords into pickup/dropoff) and onChanged
 * so it can refresh its saved-places list after a rename/delete.
 */
export default function SavedPlaceQuickChip({ place, onFill, onChanged, testid }) {
  const isHome = /home/i.test(place.label || "");
  const Icon = isHome ? Home : Briefcase;
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(place.label || "");
  const [coords, setCoords] = useState({ lat: place.lat, lng: place.lng, address: place.address });
  const [busy, setBusy] = useState(false);
  const wrap = useRef(null);

  useEffect(() => {
    if (!editing) return;
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setEditing(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [editing]);

  const rename = async () => {
    if (busy) return;
    const patch = {};
    if (label.trim() && label !== place.label) patch.label = label.trim();
    if (coords.lat !== place.lat || coords.lng !== place.lng) {
      patch.lat = coords.lat; patch.lng = coords.lng; patch.address = coords.address;
    }
    if (Object.keys(patch).length === 0) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.patch(`/favorites/${place.favorite_id}`, patch);
      toast.success("Saved");
      onChanged && onChanged();
      setEditing(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (busy) return;
    if (!window.confirm(`Delete saved place "${place.label}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/favorites/${place.favorite_id}`);
      toast.success("Deleted");
      onChanged && onChanged();
      setEditing(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div ref={wrap} className="relative">
      <button
        type="button"
        onClick={onFill}
        className="inline-flex items-center gap-2 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-electric-teal/40 transition px-4 py-2.5 text-start"
        data-testid={testid}
      >
        <div className="w-8 h-8 rounded-full bg-electric-teal/15 text-electric-teal flex items-center justify-center shrink-0">
          <Icon size={14} strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-black text-white leading-tight">{place.label}</div>
          <div className="text-[10px] text-white/50 truncate max-w-[160px]">{(place.address || "").split(",")[0]}</div>
        </div>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setEditing((v) => !v); }}
        className="absolute -top-1 -end-1 w-6 h-6 rounded-full bg-obsidian border border-white/15 hover:border-electric-teal/40 flex items-center justify-center text-white/60 hover:text-electric-teal transition"
        data-testid={`${testid}-edit`}
        aria-label="Edit saved place"
      >
        <Settings size={11} />
      </button>
      {editing && (
        <div
          className="absolute z-50 start-0 top-full mt-2 bg-obsidian border border-white/15 rounded-xl p-3 shadow-[0_16px_48px_-8px_rgba(0,0,0,0.9)] min-w-[280px]"
          data-testid={`${testid}-editor`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest font-black text-white/50">Edit saved place</div>
            <button type="button" onClick={() => setEditing(false)} className="text-white/50 hover:text-white" aria-label="Close">
              <X size={11} />
            </button>
          </div>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={40}
            className="w-full bg-black/40 border border-white/10 rounded-lg px-2.5 py-2 text-sm text-white outline-none focus:border-electric-teal"
            placeholder="e.g. Mum's house"
            data-testid={`${testid}-label-input`}
          />

          {/* Mini map picker — drag the teal pin to relocate. On dragend we
              reverse-geocode so the address stays in sync with the pin. */}
          <div className="mt-2 rounded-lg overflow-hidden border border-white/10" style={{ height: 160 }} data-testid={`${testid}-map`}>
            <MapContainer
              center={[coords.lat || 32.0853, coords.lng || 34.7818]}
              zoom={15}
              scrollWheelZoom={false}
              zoomControl={false}
              attributionControl={false}
              style={{ width: "100%", height: "100%" }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <CenterOn center={coords.lat && coords.lng ? [coords.lat, coords.lng] : null} />
              {coords.lat && coords.lng && (
                <Marker
                  position={[coords.lat, coords.lng]}
                  icon={dragPinIcon}
                  draggable
                  eventHandlers={{
                    dragend: async (e) => {
                      const { lat, lng } = e.target.getLatLng();
                      setCoords((c) => ({ ...c, lat, lng, address: "Adjusting…" }));
                      const label = await reverseGeocode(lat, lng);
                      setCoords({ lat, lng, address: label || `${lat.toFixed(5)}, ${lng.toFixed(5)}` });
                    },
                  }}
                />
              )}
            </MapContainer>
          </div>
          <div className="mt-1.5 text-[10px] text-white/50 inline-flex items-center gap-1">
            <MapPinIcon size={10} className="text-electric-teal" />
            <span className="truncate max-w-[220px]" data-testid={`${testid}-map-address`}>
              {(coords.address || place.address || "").split(",").slice(0, 2).join(",")}
            </span>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={rename}
              disabled={busy || !label.trim() || label === place.label}
              className="rounded-lg bg-electric-teal text-obsidian px-3 py-2 text-[11px] font-black inline-flex items-center justify-center gap-1 disabled:opacity-40 hover:bg-electric-teal/90"
              data-testid={`${testid}-save`}
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} strokeWidth={3} />} Save
            </button>
            <button
              type="button"
              onClick={del}
              disabled={busy}
              className="rounded-lg border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 px-3 py-2 text-[11px] font-black inline-flex items-center justify-center gap-1 disabled:opacity-40"
              data-testid={`${testid}-delete`}
            >
              <Trash2 size={11} /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
